import { Button, ProgressCircle, Text } from '@react-spectrum/s2';
import {
	useId,
	useLayoutEffect,
	useRef,
	useState,
	type CSSProperties,
} from 'react';
import {
	Button as RACButton,
	Focusable,
	Tooltip,
	TooltipTrigger,
} from 'react-aria-components';

import { LiveDuration, MetricGauge } from '@/spaces/campaigns/StrategyBits';
import {
	creativityBand,
	creativityBandHint,
	creativityBandLabel,
	generatingGoalLabel,
	tierInk,
	timeAgo,
} from '@/spaces/campaigns/strategyFormat';
import {
	proposalHeadline,
	type PendingProposal,
} from '@/spaces/campaigns/usePendingProposals';
import type { StrategyCardNorthStar } from '@/spaces/campaigns/useStrategies';
import { useStrategyCard } from '@/spaces/campaigns/useStrategies';
import { cn } from '@/utils/cn';

import type { StrategyListItem } from '../../../../../bridge/types/strategy.ts';

import type { OrbIdentity } from './orbIdentity';
import { orbNow } from './orbRenderer';
import {
	openFlags,
	openOwnerAsks,
	dockIconStatus,
	requestsWaitingLabel,
} from './strategyDockData';
import { StrategyOrb } from './StrategyOrb';

// The footer meta line — the state a reader ACTS on is carried entirely by the CTA button
// (Generating… / Activate / Retry), so there is no redundant status dot here. This slot only
// adds what the button can't say: a live elapsed timer while generating (the loading
// experience), an honest failure line (with room for a message), or the generated-time once
// ready. No StatusLight — the button is the single state affordance.
function FooterMeta({ item }: { item: StrategyListItem }) {
	const status = dockIconStatus(item);
	if (status.kind === 'generating') {
		return (
			<span className="flex items-center gap-1.5 text-xs text-(--text-muted)">
				<ProgressCircle
					isIndeterminate
					size="S"
					aria-label="Generating strategy"
				/>
				<LiveDuration coarse startedAt={item.started_at} />
			</span>
		);
	}
	if (status.kind === 'failed') {
		return (
			<span className="truncate text-xs text-red-600">
				Something went wrong — try again.
			</span>
		);
	}
	return (
		<span className="truncate text-xs text-(--text-muted)">
			Generated {timeAgo(item.completed_at ?? item.started_at)}
		</span>
	);
}

// The tier (Proven / Bold / Big swing) as a TAG — a small pill, mirroring the existing chip
// pattern (ArtifactChip). Each tier is written in its OWN hue — the same aurora-blend colour
// its orb is painted with — so the three read as three kinds of bet at a glance instead of
// three identical grey chips. Colour alone never carries the meaning: the label says it.
// Hovering/focusing reveals a tooltip DEFINING the tier (canonical RAC TooltipTrigger +
// Focusable, in the app's dark chip); `cursor-help` is the affordance, as on GlossedText's
// terms. A sibling of the click-target — never nested in the big open button.
function TierTag({ item }: { item: StrategyListItem }) {
	const band = creativityBand(item.creativity_level);
	const label = creativityBandLabel(band);
	const hint = creativityBandHint(band);
	return (
		<TooltipTrigger delay={300}>
			<Focusable>
				<span
					role="button"
					tabIndex={0}
					data-testid="strategy-card-tier"
					style={{ '--tier-ink': tierInk(band) } as CSSProperties}
					className="tier-pill relative z-10 inline-flex cursor-help
						items-center rounded-full px-2 py-0.5 text-xs font-semibold
						outline-none"
				>
					{label}
				</span>
			</Focusable>
			<Tooltip
				placement="top"
				offset={6}
				className="z-50 max-w-xs rounded-md bg-gray-800 px-2.5 py-1.5 text-xs
					leading-snug text-white shadow-md"
			>
				<span className="font-semibold">{label}:</span> {hint}
			</Tooltip>
		</TooltipTrigger>
	);
}

// A chevron that points down when collapsed, up when expanded.
function Chevron({ open }: { open: boolean }) {
	return (
		<svg
			viewBox="0 0 16 16"
			width={13}
			height={13}
			aria-hidden
			className={cn(
				'shrink-0 transition-transform duration-150',
				open && 'rotate-180',
			)}
		>
			<path
				d="M4 6l4 4 4-4"
				fill="none"
				stroke="currentColor"
				strokeWidth={1.5}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

// One campaign's "the move" — the concrete thing that happens, which is what an owner deciding
// whether to activate actually needs. Two lines by default with its OWN "More" toggle, so every
// campaign stays visible collapsed instead of one long move eating the whole body.
//
// The clamp lives on this leaf <p> and nowhere else. On a multi-child container `line-clamp-N`
// clamps by TOTAL line count (moves 2..N vanish entirely, ellipsis lands on move 1 as if it were
// the only one), and it silently no-ops if the clamped element's own `display` is overridden —
// twMerge does not treat `line-clamp-*` and `flex` as one conflict group, so `cn()` keeps both and
// the later-emitted `display` wins. Rows are stacked with `space-y-*` by the caller for the same
// reason. Both failure modes measured in Chromium 145 + 149.
//
// Rows are delineated by the card's own hairline (the footer rule), not a tinted block: grey is the
// DOCK's surface and white glass is the card, so tinting the body inverts that figure/ground — and a
// block that looks like a sub-card would still open the strategy, since the whole card is one click
// target. The toggle is a RAISED control (z-10) above that stretched overlay.
function MoveTray({
	title,
	move,
	first,
}: {
	title: string;
	move: string;
	first: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [overflows, setOverflows] = useState(false);
	const ref = useRef<HTMLParagraphElement>(null);
	const bodyId = useId();

	// Measure whether the clamped paragraph is taller than its 2-line box. Re-check on text
	// change; a ResizeObserver keeps it honest across column-width reflow (the resizable split).
	useLayoutEffect(() => {
		const el = ref.current;
		// A blank aim renders a fallback line, which must never carry a dead "More".
		if (!el || !move) return;
		const measure = () => {
			if (open) return; // only meaningful while clamped
			setOverflows(el.scrollHeight - el.clientHeight > 1);
		};
		measure();
		const ro =
			typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(measure)
				: null;
		if (ro && el) ro.observe(el);
		return () => ro?.disconnect();
	}, [move, open]);

	return (
		<div
			data-testid="strategy-card-move"
			className={cn(!first && 'border-t border-black/[0.06] pt-3')}
		>
			{/* The campaign's name in sentence case: it says WHOSE move this is, and it is the
			    differentiator between rows. Primary ink, not muted — muted on a tint measures
			    4.37:1 and fails AA — and never uppercase, since an 18-word title that then
			    truncates would be shouting a fragment. */}
			<p className="truncate text-xs font-semibold text-(--text-primary)">
				{title}
			</p>
			{move ? (
				<p
					id={bodyId}
					ref={ref}
					className={cn(
						'mt-1 text-sm leading-snug text-(--text-secondary)',
						!open && 'line-clamp-2',
					)}
				>
					{move}
				</p>
			) : (
				<p className="mt-1 text-sm leading-snug text-(--text-muted)">
					No detail yet.
				</p>
			)}
			{(overflows || open) && (
				<RACButton
					onPress={() => setOpen((v) => !v)}
					aria-expanded={open}
					aria-controls={bodyId}
					// A card carries up to 6 of these, so the visible "More" is not a usable name
					// on its own — this is what tells a screen reader WHICH campaign it expands.
					aria-label={`${open ? 'Less' : 'More'} about ${title}`}
					className="relative z-10 mt-1 flex min-h-6 cursor-pointer items-center
						gap-1 text-xs font-semibold text-(--text-muted) transition-colors
						outline-none hover:text-(--text-primary)
						focus-visible:text-(--text-primary)"
				>
					{open ? 'Less' : 'More'}
					<Chevron open={open} />
				</RACButton>
			)}
		</div>
	);
}

// Scale the centered target to its digit count so the EXACT number (never an abbreviation that
// could hide 92,334 behind "92k") always fits inside the 56px ring. Longer numbers step down a
// size; the "90,000"/"2,000" common case stays comfortably legible.
function gaugeNumberSize(target: number): string {
	const len = target.toLocaleString().length;
	if (len <= 4) return 'text-sm';
	if (len <= 6) return 'text-xs';
	return 'text-[10px]';
}

// The north-star goal shown VISUALLY — the design-system gold MetricGauge ring (the sanctioned
// gold signature) with the target number at its center, the metric name beside it (the number is
// already the gauge center, so it isn't repeated below the name). The ring's faint full track IS
// the goal; a solid gold arc overlays only when a real baseline exists (honest static progress,
// not live). No baseline → the ring + number still reads as a goal (never an empty bar).
function NorthStar({
	northStar,
	current,
}: {
	northStar: StrategyCardNorthStar;
	current?: number | null;
}) {
	const { name, target, baseline } = northStar;
	return (
		<div className="flex items-center gap-3">
			<MetricGauge
				target={target}
				baseline={baseline}
				current={current}
				size={56}
			>
				<span
					className={cn(
						'px-1 leading-none font-bold text-(--text-primary) tabular-nums',
						gaugeNumberSize(target),
					)}
				>
					{target.toLocaleString()}
				</span>
			</MetricGauge>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<p
					className="text-[11px] leading-none font-semibold tracking-wide
						text-(--text-muted) uppercase"
				>
					North star
				</p>
				<p className="text-sm leading-snug font-semibold text-(--text-primary)">
					{name}
				</p>
			</div>
		</div>
	);
}

// One strategy as a card. The tier reads as a TAG up top; the wish is the PROMINENT hero title;
// the north-star goal is shown VISUALLY as a gold gauge ring; a single hairline fences off a
// footer carrying the status, the generated-time, and the CTA. The CTA opens the REVIEWED flow
// The strategy's unanswered agent asks, rendered on the strategy they belong to.
//
// Per strategy rather than one list across all of them: an ask is always about one strategy,
// and a single pooled list makes the owner cross-reference which. It also keeps this next to
// the card's own state, so "what is it doing" and "what does it want from me" are read
// The owner's own parts of this strategy's campaigns, standing until they answer.
//
// A route that exists whether or not the card is still unread. The dock only opens the
// notification list while something is unseen, so without this a reminder the owner has read
// once becomes unreachable — and an unreachable reminder is not a reminder.
// The route into this strategy's notification list, on the strategy itself.
//
// A bar orb was the list's only door, and only a running strategy earns one. So pausing a strategy
// took away the door while leaving the request open. This is that door, and it does not read
// `status`: the request is what matters, not the lifecycle.
//
// It appears only when it leads somewhere the card does not already print. `CardOwnerAsks` below
// shows each owner ask in full, so a card holding nothing else would offer two ways to read one
// message. The GATE therefore counts what is unprinted, while the LABEL counts every open request,
// because the list shows them all.
function CardOpenRequests({
	item,
	onOpenFlags,
	ownerAsksArePrinted,
}: {
	item: StrategyListItem;
	onOpenFlags?: (id: string) => void;
	// Whether CardOwnerAsks below will actually render. It needs its own handler to render at all,
	// and "printed" has to mean printed: without this, a card that prints nothing would still treat
	// its owner asks as covered and hide the only route to them.
	ownerAsksArePrinted: boolean;
}) {
	const open = openFlags(item);
	const printed = new Set(
		ownerAsksArePrinted ? openOwnerAsks(item).map((f) => f.id) : [],
	);
	const unprinted = open.filter((f) => !printed.has(f.id));
	if (unprinted.length === 0 || !onOpenFlags) return null;

	return (
		<div data-testid="strategy-card-open-requests" className="mt-4">
			{/* `relative z-10` like every other control in this card: the card's open affordance
			    stretches an `after:inset-0 after:z-[1]` overlay across the whole surface, so a
			    control without it is covered and the press opens the strategy instead. */}
			<Button
				variant="secondary"
				size="S"
				onPress={() => onOpenFlags(item.id)}
				UNSAFE_className="relative z-10"
			>
				<Text>{requestsWaitingLabel(open.length)}</Text>
			</Button>
		</div>
	);
}

function CardOwnerAsks({
	item,
	onOpenOwnerAsk,
}: {
	item: StrategyListItem;
	onOpenOwnerAsk?: (actionId: string, message: string) => void;
}) {
	const asks = openOwnerAsks(item);
	if (asks.length === 0 || !onOpenOwnerAsk) return null;

	return (
		<div
			data-testid="strategy-card-owner-asks"
			className="mt-4 flex flex-col gap-2"
		>
			{asks.map((ask) => (
				<button
					key={ask.id}
					type="button"
					data-testid={`strategy-card-owner-ask-${ask.action_id}`}
					onClick={() => onOpenOwnerAsk(ask.action_id ?? '', ask.message)}
					// `relative z-10` like every other control in this card: the card's open
					// affordance stretches an `after:inset-0 after:z-[1]` overlay across the
					// whole surface, so a row without it is covered and every press opens the
					// strategy instead of the conversation.
					className="relative z-10 flex cursor-pointer flex-col gap-1 rounded-lg
						border border-black/[0.08] bg-black/[0.02] px-3 py-2.5 text-left
						hover:bg-black/[0.04]"
				>
					<span
						className="text-[11px] font-semibold tracking-wide
							text-(--text-muted) uppercase"
					>
						Your part
					</span>
					<span className="text-sm leading-snug text-(--text-primary)">
						{ask.message}
					</span>
				</button>
			))}
		</div>
	);
}

function SeedDetails({ params }: { params: Record<string, unknown> }) {
	const focus = String(params.recommended_focus || '');
	const reasoning = String(params.reasoning || '');
	const channel = String(params.channel_suggestion || '');
	const metrics = Array.isArray(params.metrics_to_carry)
		? (params.metrics_to_carry as string[])
		: [];

	return (
		<div
			className="mt-1 flex flex-col gap-1 rounded border border-black/[0.06]
				bg-black/[0.03] px-2 py-1.5 text-xs text-(--text-muted)"
		>
			{focus ? (
				<span>
					<span className="font-medium">Focus:</span> {focus}
				</span>
			) : null}
			{reasoning ? (
				<span>
					<span className="font-medium">Why:</span> {reasoning}
				</span>
			) : null}
			{channel ? (
				<span>
					<span className="font-medium">Channel:</span> {channel}
				</span>
			) : null}
			{metrics.length > 0 ? (
				<span>
					<span className="font-medium">Carry forward:</span>{' '}
					{metrics.join(', ')}
				</span>
			) : null}
		</div>
	);
}

// together.
//
// The buttons are the short path — most asks are a yes or a not-now. The body opens a thread
// for the ones worth talking through.
function CardProposals({
	proposals = [],
	strategyTitle,
	onRespond,
	onDiscuss,
}: {
	proposals?: PendingProposal[];
	strategyTitle?: string;
	onRespond?: (id: string, decision: 'approve' | 'reject') => Promise<void>;
	onDiscuss?: (p: PendingProposal, strategyTitle?: string) => void;
}) {
	// Keyed by id rather than one boolean, so answering one ask does not freeze the others.
	const [pending, setPending] = useState<Record<string, boolean>>({});
	const [failed, setFailed] = useState<Record<string, string>>({});
	if (proposals.length === 0) return null;

	const respond = async (id: string, decision: 'approve' | 'reject') => {
		if (!onRespond) return;
		setPending((p) => ({ ...p, [id]: true }));
		setFailed((f) => ({ ...f, [id]: '' }));
		try {
			await onRespond(id, decision);
		} catch {
			// The row stays put with its reason showing. Dropping it on failure would read as
			// done, which is the one thing it definitely is not.
			setFailed((f) => ({ ...f, [id]: "That didn't go through. Try again." }));
		} finally {
			setPending((p) => ({ ...p, [id]: false }));
		}
	};

	return (
		<div
			data-testid="strategy-card-proposals"
			className="mt-4 flex flex-col gap-2"
		>
			{proposals.map((p) => {
				const busy = Boolean(pending[p.id]);
				return (
					<div
						key={p.id}
						data-testid="strategy-card-proposal"
						className="flex flex-col gap-2 rounded-lg border border-black/[0.08]
							bg-black/[0.02] px-3 py-2.5"
					>
						<button
							type="button"
							className="relative z-10 flex flex-col items-start gap-1
								text-left"
							onClick={() => onDiscuss?.(p, strategyTitle)}
						>
							<span className="text-sm font-semibold text-(--text-primary)">
								{proposalHeadline(p)}
							</span>
							{p.change_type === 'recommend_next_step' && p.recommended_step ? (
								<>
									<span className="text-xs text-(--text-muted)">
										{p.recommended_step.description}
									</span>
									{p.recommended_step.sequence_note ? (
										<span className="text-xs text-(--text-muted) italic">
											{p.recommended_step.sequence_note}
										</span>
									) : null}
									{p.rationale ? (
										<span className="text-xs text-(--text-muted)">
											{p.rationale}
										</span>
									) : null}
									{p.recommended_step.applier === 'propose_new_strategy' &&
									p.recommended_step.applier_params ? (
										<SeedDetails params={p.recommended_step.applier_params} />
									) : null}
								</>
							) : p.rationale ? (
								<span className="text-xs text-(--text-muted)">
									{p.rationale}
								</span>
							) : null}
						</button>
						{failed[p.id] ? (
							<span className="text-xs text-red-600">{failed[p.id]}</span>
						) : null}
						<div className="relative z-10 flex gap-2">
							<Button
								variant="accent"
								size="S"
								isPending={busy}
								isDisabled={busy}
								onPress={() => void respond(p.id, 'approve')}
							>
								<Text>Yes, do it</Text>
							</Button>
							<Button
								variant="secondary"
								size="S"
								isDisabled={busy}
								onPress={() => void respond(p.id, 'reject')}
							>
								<Text>Not now</Text>
							</Button>
						</div>
					</div>
				);
			})}
		</div>
	);
}

// (never a one-click go-live — the setup card owns that). The title + body are click-targets that
// open detail; the tier tag and CTA are siblings (no button-in-button).
export function StrategyCard({
	item,
	identity,
	enabled,
	currentValue,
	onOpenDetail,
	onOpenFlags,
	onOpenOwnerAsk,
	onReview,
	onActivate,
	onPause,
	proposals,
	onRespondProposal,
	onDiscussProposal,
}: {
	item: StrategyListItem;
	// The north-star's latest measured value, when the daily worker has written one. Passed
	// down from the menu's single cross-strategy fetch rather than fetched per card.
	currentValue?: number | null;
	// The strategy's face, resolved from the owner's full history. Never derived here: this card
	// is rendered from a list the menu has filtered, so a rank computed locally would give the
	// same strategy a different face than the dock bar shows for it.
	identity: OrbIdentity;
	// Gate the per-card full-run fetch (feature flag / list authoritative).
	enabled: boolean;
	onOpenDetail: (id: string) => void;
	// Optional here, unlike on ListView: three test render sites build a card directly, and this
	// mirrors `onOpenOwnerAsk?` beside it.
	onOpenFlags?: (id: string) => void;
	onOpenOwnerAsk?: (actionId: string, message: string) => void;
	// Takes the card's own title so the thread it opens can be named immediately.
	// Undefined for a run with no `wish` yet, which the caller treats as "no title".
	onReview?: (id: string, title?: string) => void;
	// Starts the strategy's daily loop. Absent → the CTA falls back to opening detail.
	onActivate?: (id: string) => Promise<void>;
	// Stops an active strategy's daily loop.
	onPause?: (id: string) => Promise<void>;
	// This strategy's unanswered agent asks, already filtered to it by the list.
	proposals?: PendingProposal[];
	onRespondProposal?: (
		id: string,
		decision: 'approve' | 'reject',
	) => Promise<void>;
	onDiscussProposal?: (p: PendingProposal, strategyTitle?: string) => void;
}) {
	const card = useStrategyCard(item.id, item.status, enabled);
	// An active run has a plan worth showing just as much as a complete one.
	const hasPlan = item.status === 'complete' || item.status === 'active';
	const generating = item.status === 'generating';
	// A run's own title, once it has one. Absent for the whole early generating window (the
	// plan — and so `strategy.wish` — is written only at the model's first submit), and absent
	// for a run that failed before ever submitting.
	const title = item.wish?.trim();
	// What stands in for a title the run doesn't have. The owner's own goal when we know it —
	// the one fact here that is theirs and appears nowhere else on the card — and otherwise a
	// plain statement of what is happening. Never a placeholder noun: a card that doesn't know
	// its name must not print one.
	const untitledLine = title
		? null
		: (generatingGoalLabel(item.goal_outcome, item.goal_target) ??
			(generating ? 'Building this strategy' : 'This strategy has no title'));
	const cta = deriveCardCta(item);
	const open = () => onOpenDetail(item.id);
	const [busy, setBusy] = useState(false);
	// Pointer state for the avatar. `hovered` is state because it changes what renders; the press
	// moment is a ref because only the canvas reads it, and re-rendering the card on every click
	// would buy nothing.
	const [orbHovered, setOrbHovered] = useState(false);
	const orbPressedAt = useRef<number | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const press = () => {
		if (cta.action === 'review' && onReview) {
			onReview(item.id, title);
			return;
		}
		const run =
			cta.action === 'activate'
				? onActivate
				: cta.action === 'pause'
					? onPause
					: undefined;
		if (!run) {
			open();
			return;
		}
		setBusy(true);
		setActionError(null);
		void run(item.id)
			.catch((err: unknown) => {
				setActionError(
					err instanceof Error
						? err.message
						: "That didn't work. Please try again.",
				);
			})
			.finally(() => setBusy(false));
	};

	return (
		<div
			// Hover and press on the whole card drive the avatar's sash, the same as in the dock:
			// the card IS one big click target, so its orb should answer the pointer anywhere on
			// it rather than only over the 24px canvas. The orb asks this element — not the
			// canvas — whether the pointer is still over it.
			data-orb-hover-owner
			onPointerEnter={() => setOrbHovered(true)}
			onPointerLeave={() => setOrbHovered(false)}
			onPointerDown={() => {
				orbPressedAt.current = orbNow();
			}}
			data-testid="strategy-card"
			// The run this card stands for — the only stable way a test can assert WHICH
			// strategies the dock is showing (wishes repeat across regenerations).
			data-run-id={item.id}
			// The orb's motion says whether this strategy is working or stalled, and it is
			// drawn to an aria-hidden canvas, so nothing about it reaches the DOM. This
			// mirrors the kind the orb is rendering with, so a test can assert a stalled
			// strategy looks stalled instead of trusting a screenshot.
			data-orb-kind={dockIconStatus(item).kind}
			// The correct NON-announcing signal that this card's content is still settling. No
			// role="status"/aria-live here on purpose: a batch renders three of these and the
			// list re-polls every 4s, so live regions would announce the same sentence over and
			// over. The stand-in line is real visible text a reader reaches by reading the card.
			aria-busy={generating || undefined}
			className={cn(
				`glass-card-sm group relative flex flex-col p-5 transition-transform
				duration-150 ease-out
				focus-within:shadow-[0_0_0_3px_rgba(59,99,251,0.18)]
				active:scale-[0.985] motion-reduce:transform-none
				motion-reduce:transition-none`,
			)}
		>
			{/* Top row: the orb + tier TAG, siblings of the click-target so neither nests an
			    interactive inside the big open button. */}
			<div className="flex items-center justify-between gap-2">
				<div className="flex min-w-0 items-center gap-2">
					{/* The SAME avatar this strategy shows in the dock — same component, same
					    face, same status motion — so a row is recognisable in both places. */}
					<StrategyOrb
						identity={identity}
						status={dockIconStatus(item)}
						interactive={{
							hovered: orbHovered,
							pressedAt: orbPressedAt.current,
						}}
						className="[--orb-size:24px]"
					/>
					<TierTag item={item} />
				</div>
			</div>

			{/* The whole card is ONE click-target: this button's ::after overlay stretches over
			    the entire (relative) card, so clicking anywhere that isn't a raised control opens
			    detail. It therefore renders unconditionally, even for a run with no title yet —
			    removing it would make the card unclickable. The wish IS the title — the hero. */}
			<button
				type="button"
				data-testid="strategy-card-open"
				aria-label={`Open ${title || 'strategy'}`}
				onClick={open}
				className="mt-3 block w-full cursor-pointer text-left outline-none
					after:absolute after:inset-0 after:z-[1] after:rounded-[inherit]
					after:content-['']"
			>
				{/* Capped at 2 lines (a full-width row fits the wish in one or two), but NOT
				    height-reserved: stacked rows have no side-by-side neighbor to align to, so a
				    min-height would just leave a gap under short titles. */}
				{title ? (
					<p
						data-testid="strategy-card-primary"
						className="line-clamp-2 text-lg leading-snug font-semibold
							text-(--text-primary)"
					>
						{title}
					</p>
				) : generating ? (
					/* A run still being written has no name, so nothing here may claim to be one.
					   The bar holds the title's place — h-6 (24px) is the nearest step to this
					   hero's own 24.75px line box (18px × leading-snug), so a one-line title
					   landing here barely moves the card. `research-shimmer` sits on THIS element,
					   never a wrapper: its reduced-motion guard zeroes only the ::after sweep, so
					   the element's own fill is all a reduced-motion reader has left. */
					<div
						data-testid="strategy-card-title-shimmer"
						aria-hidden
						className="research-shimmer h-6 w-3/4 rounded-md"
					/>
				) : null}
			</button>

			{/* What a card with no title of its own says instead — a SIBLING of the click-target,
			    never inside it: the button's aria-label would otherwise mask this text from a
			    screen reader, which is the only accessible content such a card has. Subordinate
			    type on purpose: the goal is not the strategy's name, and all three runs in a batch
			    share one goal, so at hero weight it would read as three duplicate cards. */}
			{!title && untitledLine && (
				<p
					data-testid="strategy-card-untitled-line"
					className="relative z-[2] mt-2 text-sm text-(--text-secondary)"
				>
					{untitledLine}
				</p>
			)}

			{/* The moves slot — every campaign's "the move", each with its own "More" tray. Height
			    follows the content: cards stack one per row in the dock menu, so there is no
			    side-by-side neighbor to align against and a reserved height would only add dead
			    space. `loading` shows skeleton rows rather than nothing, so the slot holds roughly
			    its final size instead of shoving the CTA down when the run lands. */}
			{hasPlan && (
				// mt-6 (not the mt-3 the other slots use): the campaign rows are a GROUP, so they
				// need more air under the wish than a single paragraph did, or the first row's
				// title reads as the wish's deck rather than the first item of a list.
				<div className="mt-6 w-full text-left">
					{card.state === 'loading' ? (
						<div className="space-y-3">
							{[0, 1].map((i) => (
								<div key={i} className="space-y-1.5">
									<div className="h-4 w-32 animate-pulse rounded bg-gray-100" />
									<div className="h-9 w-full animate-pulse rounded bg-gray-100" />
								</div>
							))}
						</div>
					) : card.state === 'error' ? (
						<p className="text-xs text-(--text-muted)">
							Couldn&apos;t load this strategy&apos;s campaigns.
						</p>
					) : !card.moves?.length ? (
						// A complete run CAN persist with zero campaigns (the count rule is a
						// validator violation, not a gate), so this is honest emptiness — not a
						// load failure. Same wording as the strategy screen.
						<p className="text-xs text-(--text-muted)">
							No campaigns in this strategy yet.
						</p>
					) : (
						/* space-y, never `flex flex-col gap-*`: a flex container would kill the
						   per-row clamp, and twMerge would not warn. */
						<div className="space-y-3">
							{card.moves.map((m, i) => (
								<MoveTray
									key={m.id}
									title={m.title}
									move={m.move}
									first={i === 0}
								/>
							))}
						</div>
					)}
				</div>
			)}

			{/* North-star slot. mb-4 keeps the gauge off the footer rule; the footer's mt-auto is
			    harmless in a stacked row (nothing stretches these cards) and still pins the footer
			    to the bottom if a row is ever stretched. */}
			{hasPlan && card.northStar && (
				<div className="mt-4 mb-4 w-full">
					<NorthStar northStar={card.northStar} current={currentValue} />
				</div>
			)}

			{/* What this strategy is waiting on the owner for. Sits above the footer because it
			    is the one thing on the card that is addressed TO them — everything else is a
			    report on what the strategy is doing. Absent entirely when nothing is pending. */}
			<CardOpenRequests
				item={item}
				onOpenFlags={onOpenFlags}
				ownerAsksArePrinted={Boolean(onOpenOwnerAsk)}
			/>

			<CardOwnerAsks item={item} onOpenOwnerAsk={onOpenOwnerAsk} />

			<CardProposals
				proposals={proposals}
				strategyTitle={title}
				onRespond={onRespondProposal}
				onDiscuss={onDiscussProposal}
			/>

			{/* Footer: a single hairline fences off the meta line (left — a live timer while
			    generating, a failure line, or the generated-time) and the CTA (right). The CTA
			    button is the sole state affordance; there is no redundant status dot. */}
			<div
				className="mt-auto flex items-center justify-between gap-2 border-t
					border-black/[0.06] pt-4"
			>
				<div className="flex min-w-0 items-center gap-1.5">
					{actionError ? (
						<span
							data-testid="strategy-card-activate-error"
							className="truncate text-xs text-red-600"
						>
							{actionError}
						</span>
					) : (
						<FooterMeta item={item} />
					)}
				</div>
				<Button
					variant={cta.variant}
					size="S"
					isDisabled={cta.disabled || busy}
					onPress={press}
					UNSAFE_className="relative z-10"
				>
					<Text>
						{busy
							? cta.action === 'pause'
								? 'Pausing…'
								: 'Starting…'
							: cta.label}
					</Text>
				</Button>
			</div>
		</div>
	);
}

interface CardCta {
	label: string;
	variant: 'accent' | 'secondary';
	disabled: boolean;
	// What pressing it does: start the strategy, stop it, or just open the detail.
	action: 'activate' | 'review' | 'pause' | 'open';
}

// Status → CTA. "Activate"/"Reactivate" starts the strategy's daily loop; everything else
// opens the detail. An already-running strategy must never be offered activation again — that
// would 409 and reads as if nothing happened.
// The one control that changes the strategy's state, so activating and pausing live in the
// same place. An active strategy offers Pause — the state has to be reversible from where it
// was set, or "active" is a one-way door.
function deriveCardCta(item: StrategyListItem): CardCta {
	if (item.status === 'generating') {
		return {
			label: 'Generating…',
			variant: 'secondary',
			disabled: true,
			action: 'open',
		};
	}
	if (item.status === 'failed') {
		return {
			label: 'Retry',
			variant: 'secondary',
			disabled: false,
			action: 'open',
		};
	}
	if (item.status === 'active') {
		return {
			label: 'Pause',
			variant: 'secondary',
			disabled: false,
			action: 'pause',
		};
	}
	return {
		label: 'Review and activate',
		variant: 'accent',
		disabled: false,
		action: 'review',
	};
}
